import React, { PureComponent } from "react";
import clsx from "clsx";
import moment from "moment";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
  ReferenceDot,
} from "recharts";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  RenderBackground,
  InsightsTickIndicator,
  closeAllBarchartTooltips,
} from "./../Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import {
  selectFormat,
  MAIN_COLORS,
  CustomizedYAxisTick,
  StandardTooltip,
  Error,
  formatDateFromData,
} from "./../Utils/Utils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

const COLORS = {
  // to be populated dynamically on Constructor
};

class BarChartLegendsStackedBoardReport extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
      widgetsData: props.data.data,
      widgetsInsights: [],
    };
    this.node = React.createRef();
    this.divPosition = React.createRef();

    this.updatePosition = this.updatePosition.bind(this);

    const data = props.data && props.data.hasData ? props.data.data : [];

    // populate the COLORS according for each key set inside the data/element
    if (data && data.length && data[0].groups) {
      Object.keys(data[0].groups).forEach(
        (key, index) => (COLORS[key] = MAIN_COLORS[index])
      );
    }
  }

  /**
   * Prepare the data grouping for each country with the values of totals for each platform
   * @param {*} data
   */
  renderBars = (data) => {
    let bars = [];
    let all_bars = [];
    const insights = this.state.widgetsInsights;

    if (data.length && data[0].groups) all_bars = Object.keys(data[0].groups);

    const isFixedBarWidth = data.length <= 7;

    all_bars.forEach((bar, i) => {
      bars.push(
        <Bar
          key={i}
          dataKey={"groups." + bar + ".total"}
          stackId="stack"
          barSize={isFixedBarWidth ? 35 : null}
          fill={COLORS[bar]}
          isAnimationActive={false}
          cursor="pointer"
          // only first bar needs background
          background={
            i === 0 ? (
              <RenderBackground
                insights={insights}
                showIndicator={!this.state.tooltip.show}
                node={this.node}
                i={i}
              />
            ) : null
          }
          onClick={(d) => {
            const extra = all_bars.map((k, j) => ({
              key: k,
              value: d.groups[k],
              color: COLORS[k],
            }));

            d.extra = extra;
            d.insight = null;

            d.analysis = extra;

            d.posts = d.posts || []; // TODO: get correct data from api
            // Example of post object
            // {
            //   value: 533,
            //   direction: 'up',
            //   change: 3.25,
            //   content: 'How to run a rogue government email account with an anonymous email address and a burner phone',
            //   chart: [
            //     {label: "08/13", value: 123613},
            //     {label: "08/14", value: 120169},
            //     {label: "08/15", value: 125365},
            //     {label: "08/16", value: 118187},
            //     {label: "08/17", value: 125569},
            //     {label: "08/18", value: 129546},
            //     {label: "08/19", value: 140253}
            //   ]
            // }

            const indicator = "barChartLegendsStackedBoardReport";
            d.indicator = indicator;

            this.clickBar(d, bar);
          }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${bar}-${index}`}
              fillOpacity={
                (entry.groups[bar] && entry.groups[bar].total) < 0 ? 0.5 : 1
              }
              // fillOpacity={(entry[bar]) < 0 ? 0.5 : 1}
            />
          ))}
        </Bar>
      );
    });

    return bars;
  };

  legendElement = (data) =>
    data
      .filter((d) => d !== "date")
      .map((entry, i) => (
        <span key={i} className="legend mt-2 mt-md-0">
          <span
            className="legend__icon"
            style={{ background: COLORS[entry] }}
          ></span>
          <span className="legend__text" style={{ color: COLORS[entry] }}>
            {entry}
          </span>
        </span>
      ));

  renderLegends = (data) => {
    let legends = [];
    if (data.length > 0 && data[0].groups)
      legends = Object.keys(data[0].groups).sort();
    return this.legendElement(legends);
  };

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }
  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef && this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  clickBar(d, dataKey) {
    if (
      this.state.tooltip.show &&
      d.date.date === this.state.tooltip.date.date
    ) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  getExportData = () => {
    const url = this.props.url;

    const newKeyNames =
      url.includes(
        "yes-network/viewing-daily?t=last30&metric=unique_viewers"
      ) || url.includes("yes-network/viewing-daily?t=last30&metric=view_time");

    const formattedData = [];
    this.props.data.data.map((o) => {
      const currentDate = moment(o.date.date).format("YYYY-MM-DD");

      return Object.entries(o.groups).map((i) => {
        let currentKey = i[0];
        let currentSources = Object.entries(i[1]).filter(
          (i) => i[0] !== "total"
        );

        return currentSources.map((source) =>
          formattedData.push({
            date: currentDate,
            [newKeyNames ? "device_type" : "region"]: currentKey,
            [newKeyNames ? "platform" : "source"]: source[0],
            value: source[1].value,
          })
        );
      });
    });

    return {
      data: formattedData,
      opts: {
        type: "other",
      },
    };
  };

  render() {
    const { data, title } = this.props;
    const { widgetsInsights, width } = this.state;

    const widget_data = data.data;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(widget_data);

    preparedData.forEach((d) => {
      if (d.amazon && typeof d.amazon === "number") {
        d.amazon = { total: d.amazon };
      }

      if (d["apple+"] && typeof d["apple+"] === "number") {
        d["apple+"] = { total: d["apple+"] };
      }
      if (d.ca && typeof d.ca === "number") {
        d.ca = { total: d.ca };
      }
      if (d.us && typeof d.us === "number") {
        d.us = { total: d.us };
      }
      if (d.unknown && typeof d.unknown === "number") {
        d.unknown = { total: d.unknown };
      }
    });

    return (
      <div ref={this.divPosition}>
        {this.renderLegends(preparedData)}
        <div className="d-flex mt-2">
          <ResponsiveContainer height={370} width="100%">
            <BarChart
              data={preparedData}
              margin={{ top: 12, right: 0, bottom: 15, left: -38 }}
              stackOffset="sign"
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Tooltip
                cursor={{ fill: "#eee", opacity: 0.5 }}
                content={
                  <StandardTooltip
                    render={({ data }) => {
                      if (this.state.tooltip.show) return "";

                      const payload = data(this.props);

                      if (payload !== null) {
                        const tooltip_data = payload[0].payload;
                        const total = payload.reduce(
                          (acc, cur) => acc + (cur.value || 0),
                          0
                        );

                        let datePeriod;
                        if (typeof tooltip_data.date === "string") {
                          datePeriod = tooltip_data.date;
                        } else if (typeof tooltip_data.date === "object") {
                          if (
                            tooltip_data.date.startDate &&
                            tooltip_data.date.endDate
                          ) {
                            datePeriod = `${tooltip_data.date.startDate} - ${tooltip_data.date.endDate}`;
                          } else if (tooltip_data.date.date) {
                            datePeriod = tooltip_data.date.date;
                          } else {
                            datePeriod = "Unknown date";
                          }
                        } else {
                          datePeriod = "Invalid date format";
                        }

                        return (
                          <div className="standard-tooltip">
                            {tooltip_data.date.date && (
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header">
                                  Date:
                                </span>
                                <span className="standard-tooltip__value-header">
                                  {datePeriod}
                                </span>
                              </header>
                            )}
                            <main className="standard-tooltip__main">
                              {Object.keys(payload)
                                .sort(
                                  (a, b) => payload[b].value - payload[a].value
                                )
                                .filter((key) => payload[key].value !== 0)
                                .map((key, index) => (
                                  <div
                                    className="standard-tooltip__item"
                                    key={key}
                                  >
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{ background: payload[key].color }}
                                    ></span>
                                    <span
                                      className={clsx(
                                        "standard-tooltip__key",
                                        "text-capitalize"
                                      )}
                                      style={{ color: payload[key].color }}
                                    >
                                      {payload[key].dataKey.split(".")[1]}{" "}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        payload[key].value
                                      )}
                                    </span>
                                  </div>
                                ))}
                              <div className="standard-tooltip__dilimiter1"></div>

                              <div className="standard-tooltip__item">
                                <span
                                  className={clsx(
                                    "standard-tooltip__key",
                                    "text-capitalize",
                                    "total"
                                  )}
                                >
                                  Total{" "}
                                </span>
                                <span className="standard-tooltip__value">
                                  {selectFormat("number", total)}
                                </span>
                              </div>

                              {widgetsInsights.length > 0 && (
                                <InsightsWithinTooltip
                                  data={widgetsInsights}
                                  tooltipDate={tooltip_data.date}
                                />
                              )}
                            </main>
                          </div>
                        );
                      }
                    }}
                  />
                }
              />

              {this.renderBars(preparedData)}

              <XAxis
                dataKey="date.date"
                tickLine={false}
                axisLine={false}
                interval={width >= 470 ? 0 : null}
                scale="auto"
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(d) => selectFormat("number", d)}
                tick={<CustomizedYAxisTick />}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                        // tall widgets need custom height
                      />
                    }
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>

        <div
          className="xray-tooltip-container"
          style={{
            top: "80px", //(this.state.tooltip.y - 20) + 'px',
            left:
              this.state.tooltip.x + this.state.tooltip.width + 220 <
              this.state.width
                ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                : this.state.tooltip.x -
                  getXrayTooltipPosition(
                    this.divPosition.current,
                    this.state.tooltip
                  ) +
                  "px",
            opacity: this.state.tooltip.show ? 1.0 : 0.0,
            visibility: this.state.tooltip.show ? "visible" : "hidden",
          }}
        >
          <XRayTooltip
            data={this.state.tooltip}
            insights={widgetsInsights}
            close={this.closeTooltip.bind(this)}
          />
        </div>
      </div>
    );
  }
}

export default BarChartLegendsStackedBoardReport;
